import React from 'react';
import { Form, Button, Typography, Card, message } from 'antd';
import { 
  batchUpdateOrdersToInspection, 
  updateAirwayBill,
  updateAirwayBillOrdersStatus
} from '../../../services/airwayBillService';
import { ArrowLeftOutlined, ClockCircleOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import OrderList from '../../orders/OrderList';
import OrderDetailModal from '../../orders/OrderDetailModal';
import AirwayBillHistory from '../../orders/AirwayBillHistory';
import BasicInfoSection from './sections/BasicInfoSection';
import TimeInfoSection from './sections/TimeInfoSection';
import FinancialInfoSection from './sections/FinancialInfoSection';

export const SectionHeader = ({ children }) => (
  <Typography.Text strong style={{ 
    fontSize: '14px',
    color: '#666',
    lineHeight: '32px'
  }}>
    {children}
  </Typography.Text>
);

const AirwayBillDetail = ({
  form,
  selectedAirwayBill,
  airwayBillOrders,
  loading,
  ports,
  customers,
  batches,
  onSave,
  onReturn,
  selectedOrder,
  orderModalVisible,
  onOrderClick,
  setOrderModalVisible,
  onOrdersUpdate,
  onSearch,
  totalOrders,
  pagination
}) => {
  const [historyCollapsed, setHistoryCollapsed] = React.useState(true);

  const handleInspection = async (orderIds) => {
    try {
      await batchUpdateOrdersToInspection(orderIds);
      message.success('订单状态已更新为查验件');
      onOrdersUpdate();
    } catch (error) {
      console.error('Error updating orders to inspection:', error);
      message.error('更新状态失败');
    }
  };

  return (
    <div style={{ height: '100%', padding: '16px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button 
          onClick={() => {
            // Refresh the airway bill list when returning
            onSearch && onSearch('');
            onReturn();
          }} 
          icon={<ArrowLeftOutlined />}
        >
          返回空运单列表
        </Button>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button 
            onClick={() => {
              const mawbNo = form.getFieldValue('airway_bill_number');
              if (mawbNo) {
                window.open(`https://www.mawb.cn/zh-cn/AirCargoTrack/?mawbno=${mawbNo}`, '_blank');
              }
            }}
            style={{ 
              marginRight: 8,
              background: '#7CB9E8',
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              padding: '4px 20px',
              height: '30px'
            }}
            icon={<SearchOutlined />}
          >
            航司查询
          </Button>
          <Button 
            onClick={async () => {
              try {
                const values = form.getFieldsValue();
                
                // Format date fields
                const dateFields = [
                  'document_send_time',
                  'departure_date',
                  'arrival_date',
                  'airport_pickup_time',
                  'document_review_time',
                  'processing_time',
                  'customs_clearance_time',
                  'courier_pickup_time'
                ];
                
                // Map Chinese status to English
                const statusMap = {
                  '运输中': 'SHIPPING',
                  '未审单': 'PENDING_REVIEW',
                  '待清关': 'PENDING_CUSTOMS',
                  '已提货未过机': 'PICKED_UP',
                  '已出查验未放行': 'INSPECTED_PENDING'
                };

                // Process all fields
                const processedValues = {
                  ...values,
                  // Convert numeric fields to numbers
                  bill_count: Number(values.bill_count) || 0,
                  bill_weight: Number(values.bill_weight) || 0,
                  package_count: Number(values.package_count) || 0,
                  taxed_package_count: Number(values.taxed_package_count) || 0,
                  courier_fee: Number(values.courier_fee) || 0,
                  average_tax: Number(values.average_tax) || 0,
                  total_tax: Number(values.total_tax?.toString().replace?.('%', '')) || 0,
                  additional_tax: Number(values.additional_tax) || 0,
                  // Map status to English if it exists
                  status: values.status ? statusMap[values.status] || values.status : values.status,
                  // Format date fields to always use 00:00:00 for time
                  ...Object.fromEntries(
                    dateFields.map(field => [
                      field,
                      values[field] ? values[field].format('YYYY-MM-DD 00:00:00') : null
                    ])
                  )
                };
                
                console.log('Updating airway bill with data:', processedValues);
                await updateAirwayBill(selectedAirwayBill.id, processedValues);
                
                // After saving airway bill, update all orders' status if status field exists
                const status = values.status;
                if (status) {
                  await updateAirwayBillOrdersStatus(selectedAirwayBill.id, status);
                }
                
                message.success('保存成功');
                onOrdersUpdate();
              } catch (error) {
                console.error('Error saving:', error);
                message.error('保存失败');
              }
            }}
            style={{ 
              background: '#7CB9E8',
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              padding: '4px 20px',
              height: '30px'
            }}
          >
            保存修改
          </Button>
        </div>
      </div>

      <div 
        style={{ 
          marginBottom: 16,
          cursor: 'pointer',
          userSelect: 'none'
        }}
        onClick={() => setHistoryCollapsed(!historyCollapsed)}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ClockCircleOutlined style={{ marginRight: 8 }} />
          操作历史
          <RightOutlined 
            style={{ 
              marginLeft: 8,
              transform: historyCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
              transition: 'transform 0.3s'
            }} 
          />
        </div>
        <div style={{
          height: historyCollapsed ? 0 : 'auto',
          overflow: 'hidden',
          transition: 'height 0.3s',
          marginTop: historyCollapsed ? 0 : 12,
          background: '#f5f5f5',
          padding: historyCollapsed ? 0 : 12,
          borderRadius: 4
        }}>
          {!historyCollapsed && <AirwayBillHistory history={selectedAirwayBill?.history || []} />}
        </div>
      </div>

      <Card bodyStyle={{ padding: '12px 16px' }} className="compact-form">
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 10 }}
          wrapperCol={{ span: 14 }}
          size="small"
        >
          <BasicInfoSection 
            ports={ports} 
            customers={customers} 
            batches={batches}
            form={form}
          />
          <Form.Item name="status" hidden>
            <input />
          </Form.Item>
          <TimeInfoSection 
            form={form} 
            airwayBillId={selectedAirwayBill?.id}
          />
          <FinancialInfoSection />
        </Form>
      </Card>

      <Card style={{ marginTop: 12 }} bodyStyle={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Typography.Title level={4} style={{ margin: 0, fontSize: 14 }}>订单列表</Typography.Title>
          <Button 
            onClick={() => {
              // Fetch all orders for this airway bill
              onOrdersUpdate();
              message.success('已清除筛选');
            }}
            style={{ 
              background: '#7CB9E8',
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              padding: '4px 20px',
              height: '30px'
            }}
          >
            清除筛选
          </Button>
        </div>
        <OrderList
          orders={airwayBillOrders}
          loading={loading}
          onOrderClick={onOrderClick}
          showBatchButtons={true}
          onOrdersUpdate={onOrdersUpdate}
          onInspection={handleInspection}
          totalOrders={totalOrders}
          pagination={pagination}
          onSearchResults={(results) => {
            console.log('Search results in AirwayBillDetail:', results);
            console.log('Current airway bill ID:', selectedAirwayBill?.id);
            
            // Don't update the main orders list, just show the message
            if (results.length === 0) {
              message.info('在当前空运单中未找到匹配的订单');
            } else {
              message.success(`在当前空运单中找到 ${results.length} 个订单`);
            }
          }}
          airwayBillId={selectedAirwayBill?.id}
          onPageChange={(page, pageSizeOrObj) => {
            const pageSize = typeof pageSizeOrObj === 'number' ? pageSizeOrObj : (pageSizeOrObj?.pageSize || pagination?.pageSize);
            onOrdersUpdate({ page, pageSize });
          }}
        />
      </Card>

      <OrderDetailModal
        visible={orderModalVisible}
        order={selectedOrder}
        onClose={() => setOrderModalVisible(false)}
      />
    </div>
  );
};

export default AirwayBillDetail;
